import { PaginatedResponse } from "../../../models";
import { ApiResult } from "../../core/types";

// Request Type enum - matches backend RequestType enum (HotelSuite.Domain.Enums.RequestType)
// Backend uses JsonStringEnumConverter so values are sent as strings
export enum UnifiedRequestType {
  FoodOrder = "FoodOrder",
  LateCheckout = "LateCheckout",
  ServiceRequest = "ServiceRequest",
  TransferRequest = "TransferRequest",
}

// Type-specific data interfaces
export interface FoodOrderSpecificData {
  items: Array<{
    menuItemName: string;
    quantity: number;
    price: number;
  }>;
  comment?: string | null;
  deliveryOption?: string | null;
}

export interface ServiceRequestSpecificData {
  serviceTypeDefinitionId?: string | null;
  serviceTypeName: string;
  notes?: string | null;
}

export interface LateCheckoutSpecificData {
  requestedCheckoutTime: string;
  adminNotes?: string | null;
}

export interface TransferSpecificData {
  transferType: "Predefined" | "Taxi"; // String enum from backend
  destinationName: string;
  customDestinationAddress?: string | null;
  guestNotes?: string | null;
  staffNotes?: string | null;
  vatRate?: number | null;
}

// Main unified request DTO
export interface UnifiedRequestDto {
  id: string;
  requestType: UnifiedRequestType;
  requestTypeDisplay: string;
  roomNumber: string;
  requestedAt: string;
  status: string;
  totalPrice?: number | null;
  netPrice?: number | null;
  currency?: string | null;
  paymentMethod?: string | null;
  paymentStatus?: string | null;
  pmsPostingStatus?: string | null;
  pmsPostingLastError?: string | null;
  briefDescription: string;
  typeSpecificData?:
    | FoodOrderSpecificData
    | ServiceRequestSpecificData
    | LateCheckoutSpecificData
    | TransferSpecificData;
}

// Query parameters
export interface GetUnifiedRequestsParams {
  page: number;
  pageSize: number;
  requestType?: UnifiedRequestType | null;
  status?: string;
  roomFilter?: string;
  includeClosed?: boolean;
}

// Service interface
export interface UnifiedRequestsServiceInterface {
  getRequests(
    params: GetUnifiedRequestsParams,
  ): Promise<ApiResult<PaginatedResponse<UnifiedRequestDto>>>;
}

// Helper type guards for type-specific data
export const isFoodOrderSpecificData = (
  data: unknown,
): data is FoodOrderSpecificData => {
  return (
    data !== null &&
    typeof data === "object" &&
    "items" in data &&
    Array.isArray((data as FoodOrderSpecificData).items)
  );
};

export const isServiceRequestSpecificData = (
  data: unknown,
): data is ServiceRequestSpecificData => {
  return (
    data !== null &&
    typeof data === "object" &&
    "serviceTypeName" in data &&
    typeof (data as ServiceRequestSpecificData).serviceTypeName === "string"
  );
};

export const isLateCheckoutSpecificData = (
  data: unknown,
): data is LateCheckoutSpecificData => {
  return (
    data !== null &&
    typeof data === "object" &&
    "requestedCheckoutTime" in data &&
    typeof (data as LateCheckoutSpecificData).requestedCheckoutTime === "string"
  );
};

export const isTransferSpecificData = (
  data: unknown,
): data is TransferSpecificData => {
  return (
    data !== null &&
    typeof data === "object" &&
    "destinationName" in data &&
    typeof (data as TransferSpecificData).destinationName === "string"
  );
};
